/**
 * Shared entity utilities.
 *
 * Canonical check for whether an entities object contains any meaningful data.
 * All call sites should use this instead of inline checks to keep the field
 * list consistent (currently 10 fields).
 */

const ENTITY_ARRAY_FIELDS = [
  'people',
  'locations',
  'objects',
  'organizations',
  'activity',
  'officers',
  'policiesAndLaws',
  'other',
] as const;

const SHORT_DESCRIPTION_ARRAY_FIELDS = [
  'people',
  'locations',
  'objects',
  'organizations',
] as const;

function hasMeaningfulValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasMeaningfulValue);
  }
  return value !== null && value !== undefined && value !== '';
}

/**
 * Returns true if the entities object contains at least one meaningful value
 * across all 10 entity fields (caller, people, locations, objects,
 * organizations, activity, officers, incident, policiesAndLaws, other).
 */
export function hasAnyEntities(entities: any): boolean {
  if (!entities || typeof entities !== 'object') return false;
  if (hasMeaningfulValue(entities.caller)) return true;
  if (hasMeaningfulValue(entities.incident)) return true;
  if (hasMeaningfulValue(entities.connections)) return true;
  return ENTITY_ARRAY_FIELDS.some((field) => hasMeaningfulValue(entities[field]));
}

export function getPersistableEntities<T>(entities: T | null | undefined): T | undefined {
  return hasAnyEntities(entities) ? (entities ?? undefined) : undefined;
}

export function pickSavedEntities<T>(...candidates: Array<T | null | undefined>): T | undefined {
  for (const candidate of candidates) {
    if (hasAnyEntities(candidate)) {
      return candidate as T;
    }
  }
  return undefined;
}

function normalizeShortDescriptionEntity<T>(entity: T): T {
  if (!entity || typeof entity !== 'object') {
    return entity;
  }

  const record = entity as Record<string, unknown>;
  const description = typeof record.description === 'string' ? record.description.trim() : '';
  const shortDescription = typeof record.shortDescription === 'string' ? record.shortDescription.trim() : '';

  if (!description || shortDescription) {
    return entity;
  }

  return {
    ...record,
    shortDescription: description,
  } as T;
}

export function normalizeEntitiesPayload<T>(entities: T | null | undefined): T | undefined {
  if (!entities || typeof entities !== 'object') {
    return entities ?? undefined;
  }

  const normalized = { ...(entities as Record<string, unknown>) };

  for (const field of SHORT_DESCRIPTION_ARRAY_FIELDS) {
    const value = normalized[field];
    if (Array.isArray(value)) {
      normalized[field] = value.map((item) => normalizeShortDescriptionEntity(item));
    }
  }

  return normalized as T;
}
